import { BIOMES, upcomingEvents, WORLD_EVENT_NAMES, type WorldEventKind } from '@shefira/shared';

import { element } from './kit.js';
import { uiFormat, uiText, uiTime } from './text.js';

/**
 * The next world events (ADR 0050), under the world map's legend: one short
 * line each, the event's pictogram, its hour, the village and its levels,
 * and how long until then. The calendar is fixed, so the client reads it
 * from the shared rules at its own clock; nothing is asked of the server.
 */

/** How many are listed. */
const SHOWN = 4;

/**
 * Each kind's pictogram, `assets/ui/<name>.png`; its name is the tooltip.
 */
const ICONS: Record<WorldEventKind, string> = {
  invasion: 'icon-event-invasion',
  giant: 'icon-event-giant',
  convoy: 'icon-event-convoy',
};

function hour(at: number): string {
  return uiTime(at, { hour: '2-digit', minute: '2-digit' });
}

/** « dans 23 min », « dans 1 h 40 ». */
export function untilText(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return uiFormat('dans {minutes} min', { minutes });
  const rest = minutes % 60;
  const hours = Math.floor(minutes / 60);
  return rest === 0
    ? uiFormat('dans {hours} h', { hours })
    : uiFormat('dans {hours} h {minutes}', { hours, minutes: String(rest).padStart(2, '0') });
}

/** The agenda as it stands now; rebuilt at each opening of the map. */
export function eventAgenda(now: number = Date.now()): HTMLElement {
  const section = element('div', 'world-map__agenda');
  section.append(element('h3', 'world-map__heading', 'Prochains événements'));
  const list = element('ul', 'world-map__agenda-list');
  upcomingEvents(now, SHOWN).forEach((event, k) => {
    const biome = BIOMES[event.biome];
    const name = WORLD_EVENT_NAMES[event.kind];
    const row = element('li', 'agenda-row');
    if (k === 0) row.classList.add('agenda-row--next');
    row.title = uiFormat('{event} à {village}, {hour}', {
      event: uiText(name),
      village: biome.village,
      hour: hour(event.startsAt),
    });
    const icon = element('span', 'agenda-row__icon');
    icon.style.backgroundImage = `url('/assets/ui/${ICONS[event.kind]}.png')`;
    icon.setAttribute('aria-label', uiText(name));
    const place = element('span', 'agenda-row__place');
    place.append(
      element('span', 'agenda-row__event', uiText(name)),
      element(
        'span',
        'agenda-row__level',
        uiFormat('{village} · niv. {min}-{max}', {
          village: biome.village,
          min: biome.level.min,
          max: biome.level.max,
        }),
      ),
    );
    row.append(
      icon,
      element('span', 'agenda-row__hour', hour(event.startsAt)),
      place,
      element('span', 'agenda-row__until', untilText(event.startsAt - now)),
    );
    list.append(row);
  });
  section.append(list);
  return section;
}
